import * as React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { type Course } from './Course.tsx';
import { useVma } from '../Allures/useVma.ts';
import { useAllureTable } from '../Allures/useAllureTable.ts';
import { analyserSeance } from '../Construction/seanceEngine.ts';
import { syntheseAEnregistrer } from '../Construction/seancesApi.ts';
import { formaterDureeCourse } from '../Construction/formatDuree.ts';
import { planifierCourse } from '../Planning/planningApi.ts';
import { aujourdhuiDans } from '../Planning/dates.ts';
import { fuseauDuCalendrier, useMoi } from '../Auth/moi.ts';
import { envoyerAutomatiquement } from '../Planning/intervalsIcuApi.ts';
import { useAjustementRecup } from '../Construction/ajustementRecup.ts';

// Distances proposees : celles annoncees par la course (indicateurs), avec
// l'allure de course correspondante pour estimer duree et charge.
type Distance = { cle: string; champ: keyof Course; libelle: string; km: string; allure: string };
const DISTANCES: Distance[] = [
  { cle: '5k', champ: 'indicateur_5k', libelle: '5 km', km: '5', allure: '5K' },
  { cle: '10k', champ: 'indicateur_10k', libelle: '10 km', km: '10', allure: '10K' },
  { cle: '21k', champ: 'indicateur_21k', libelle: 'Semi-marathon', km: '21.1', allure: 'HP' },
  { cle: '42k', champ: 'indicateur_42k', libelle: 'Marathon', km: '42.195', allure: 'MP' },
];
const ALLURES = [
  { code: '3K', libelle: 'Allure 3 km' },
  { code: '5K', libelle: 'Allure 5 km' },
  { code: '10K', libelle: 'Allure 10 km' },
  { code: 'HP', libelle: 'Allure semi' },
  { code: 'MP', libelle: 'Allure marathon' },
  { code: 'STEADY', libelle: 'Steady (soutenu)' },
  { code: 'EASY', libelle: 'Endurance (sortie)' },
];

/**
 * Ajout d'une course de la liste au calendrier personnel : choix de la
 * distance (et de l'allure visee), pose comme competition a la date de la
 * course (modifiable).
 */
export default function AjoutCourseCalendrier({ course, onFermer }: { course: Course | null; onFermer: () => void }) {
  const [vma] = useVma();
  const { table } = useAllureTable();
  const ajustementRecup = useAjustementRecup();
  const proposees = React.useMemo(
    () => (course ? DISTANCES.filter((d) => course[d.champ] === 'true') : []),
    [course],
  );
  const choix = proposees.length > 0 ? proposees : DISTANCES;
  const [cle, setCle] = React.useState('');
  const [allure, setAllure] = React.useState('');
  const [date, setDate] = React.useState('');
  const [enCours, setEnCours] = React.useState(false);
  const [erreur, setErreur] = React.useState<string | null>(null);
  const [ajoutee, setAjoutee] = React.useState(false);
  // Resultat de l'envoi automatique vers intervals.icu (si compte configure).
  const [envoiIcu, setEnvoiIcu] = React.useState<{ severity: 'success' | 'warning'; texte: string } | null>(null);

  // Nouvelle course : distance par defaut (la plus longue proposee) et date de la course.
  React.useEffect(() => {
    if (!course) return;
    const defaut = choix[choix.length - 1];
    setCle(defaut.cle);
    setAllure(defaut.allure);
    setDate(course.dateest ?? '');
    setErreur(null);
    setAjoutee(false);
    setEnvoiIcu(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [course]);

  const distance = DISTANCES.find((d) => d.cle === cle);
  const notation = distance ? `${distance.km}km@${allure || distance.allure}` : '';
  const analyse = React.useMemo(() => (notation ? analyserSeance(notation, vma, table) : null), [notation, vma, table]);
  const nom = course && distance ? `${course.label} — ${distance.libelle}`.substring(0, 100) : '';

  // Les jours passes du calendrier ne se modifient plus.
  // Jour courant dans le fuseau horaire de l'utilisateur.
  const moi = useMoi();
  const aujourdhui = aujourdhuiDans(fuseauDuCalendrier(moi, null));
  const datePassee = !!date && date < aujourdhui;

  const ajouter = async () => {
    if (!course || !distance || !analyse || analyse.synthese.erreur || !date || datePassee) return;
    setEnCours(true);
    setErreur(null);
    try {
      setEnvoiIcu(null);
      const p = await planifierCourse({
        date,
        nom,
        notation,
        idCourse: course.id_calendrier,
        notes: [course.city, course.province].filter(Boolean).join(', ') || null,
        ...syntheseAEnregistrer(analyse.synthese, analyse.detail, vma),
      });
      setAjoutee(true);
      const envoi = await envoyerAutomatiquement(p, vma, table, ajustementRecup);
      setEnvoiIcu(envoi.message ?? null);
    } catch (e) {
      setErreur('Ajout impossible : ' + (e as Error).message);
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Dialog open={!!course} onClose={enCours ? undefined : onFermer} fullWidth maxWidth="sm">
      <DialogTitle>Ajouter au calendrier</DialogTitle>
      <DialogContent>
        {course && (
          <>
            <Typography sx={{ fontWeight: 600 }}>{course.label}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {[course.city, course.province].filter(Boolean).join(', ')}
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>Distance</Typography>
            <ToggleButtonGroup
              exclusive
              color="primary"
              size="small"
              value={cle}
              onChange={(_e, v: string | null) => {
                if (!v) return;
                setCle(v);
                setAllure(DISTANCES.find((d) => d.cle === v)?.allure ?? '');
                setAjoutee(false);
              }}
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              {choix.map((d) => <ToggleButton key={d.cle} value={d.cle}>{d.libelle}</ToggleButton>)}
            </ToggleButtonGroup>
            {proposees.length === 0 && (
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: -1, mb: 2 }}>
                Les distances de cette course ne sont pas renseignées : choisis celle que tu cours.
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => { setDate(e.target.value); setAjoutee(false); }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: aujourdhui }}
                sx={{ minWidth: 180 }}
              />
              <TextField
                select
                label="Allure visée"
                value={allure}
                onChange={(e) => { setAllure(e.target.value); setAjoutee(false); }}
                sx={{ minWidth: 200 }}
                helperText="Pour estimer la durée et la charge"
              >
                {ALLURES.map((a) => <MenuItem key={a.code} value={a.code}>{a.libelle}</MenuItem>)}
              </TextField>
            </Box>

            {analyse && !analyse.synthese.erreur && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <code>{notation}</code> — {formaterDureeCourse(analyse.synthese.dureeTotaleS)} estimées ·{' '}
                {Math.round(analyse.synthese.chargeUA)} UA · RPE {analyse.synthese.rpeSeance}
              </Typography>
            )}
            {analyse?.synthese.erreur && <Alert severity="error" sx={{ mt: 1 }}>{analyse.synthese.erreur}</Alert>}
            {!date && <Alert severity="warning" sx={{ mt: 1 }}>Indique la date de la course.</Alert>}
            {datePassee && !ajoutee && <Alert severity="warning" sx={{ mt: 1 }}>Cette date est passée : le calendrier ne se modifie plus pour les jours passés.</Alert>}
            {erreur && <Alert severity="error" sx={{ mt: 2 }}>{erreur}</Alert>}
            {ajoutee && (
              <Alert severity="success" sx={{ mt: 2 }}>
                « {nom} » ajoutée à ton calendrier.{' '}
                <RouterLink to="/planning">Voir le calendrier</RouterLink>
              </Alert>
            )}
            {envoiIcu && (
              <Alert severity={envoiIcu.severity} sx={{ mt: 1 }}>{envoiIcu.texte}</Alert>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFermer} disabled={enCours}>{ajoutee ? 'Fermer' : 'Annuler'}</Button>
        {!ajoutee && (
          <Button
            variant="contained"
            onClick={ajouter}
            disabled={enCours || !date || datePassee || !analyse || !!analyse.synthese.erreur}
          >
            Ajouter
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
